import { useCallback } from 'react';
import {
    AppointmentLoanerOrRentalOption,
    AppointmentNotificationName,
    AppointmentType,
    BusinessType,
    Country,
    DayOfWeek,
    DealerStatus,
    DmsType,
    HistoryActionType,
    HunterAuditResult,
    HunterResult,
    HunterTestResult,
    KioskStatus,
    KpiReportMetricType,
    KpiReportViewType,
    LoginLocation,
    Meridiem,
    NotificationDeliveryStatusViewAs,
    PaymentProvider,
    RecallType,
    RODepartment,
    ServiceCategory,
    ServiceType,
    ServiceTypeValues,
    UnassignedServicesFilter,
    UnassignedServicesFilterValues,
    SpgPricing,
    Timezone,
    TransportationOptionSubtype,
    TransportationOptionType,
    UserChangeActionTypes,
    UserPermissions,
    WalkInDepartment,
    WalkInsOption,
    KeyDropoffOption,
    MobilePathOption,
    VinOption,
    TagNumberOption,
    BlackbookAdjustmentMethod,
    ValuationScreen,
    MobileValuationScreen,
    CRMProvider,
} from '../Enums';
import { useLocalization } from '../../Contexts/Localization';
import {
    Appointment,
    DealerGeneralSettings,
} from '../APIObjects';
import {
    GetMeridiemLabelHook,
    GetKioskStatusLabelHook,
    GetRoDepartmentLabelHook,
    GetUserChangeHistoryLabelHook,
    GetWalkInDepartmentLabelHook,
    GetWalkInsOptionsLabelHook,
    GetKeyDropoffOptionsLabelHook,
    GetMobilePathOptionsLabelHook,
    GetVinOptionsLabelHook,
    GetTagNumberOptionsLabelHook,
    GetHunterTestResultLabelHook,
    GetHunterResultLabelHook,
    GetHunterAuditResultLabelHook,
    GetDayOfWeekLabelHook,
    GetSpgPricingLabel,
    GetServiceCategoryLabel,
    GetServiceTypeLabelHook,
    GetUnassignedServicesFilterLabelHook,
    GetKpiReportViewTypeLabelsHook,
    GetKpiReportMetricTypeLabelsHook,
    GetBlackbookAdjustmentMethodLabelHook,
    GetValuationScreenLabelHook,
    GetMobileValuationScreenLabelHook,
    GetCRMProviderLabelHook,
    GetTransportationOptionSubtypeLabelHook,
    GetTransportationOptionTypeLabelHook,
    GetAppointmentNotificationNameLabelHook,
    GetNotificationDeliveryStatusLabelHook,
    GetNotificationDeliveryStatusViewAsLabelHook,
} from './Labels.types';
import {
    AppType,
    useAppCoreState,
} from '../../Contexts/AppCore';
import { formatPhoneNumber } from '../../CommonUtils/Formatter';

export const useGetDmsTypeLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: DmsType | null | undefined,
        showNoneLabel: boolean = false) => {
        switch (e) {
            case DmsType.AM:
                return Localization.Automate;
            case DmsType.AS:
                return Localization.Autosoft;
            case DmsType.CDF:
                return Localization.CDK;
            case DmsType.DB:
                return Localization.Dealerbuilt;
            case DmsType.DT:
                return Localization.Dealertrack;
            case DmsType.RR:
                return Localization.Reynolds;
            case DmsType.PB:
                return Localization.PBS;
            case DmsType.QI:
                return Localization.Quorum;
            case DmsType.SI:
                return Localization.Serti;
            case DmsType.None:
                return showNoneLabel ? Localization.None : '';
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetPermissionLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: UserPermissions) => {
        switch (e) {
            case UserPermissions.ActivityDashboardAccess:
                return Localization.ActivityDashboard;
            case UserPermissions.ActivityDashboard_ViewReceiptTab:
                return Localization.ActivityDashboardViewReceiptTab;
            case UserPermissions.ActivityDashboard_AuditTrailTab:
                return Localization.ActivityDashboardAuditTrailTab;
            case UserPermissions.SummaryDashboardAccess:
                return Localization.SummaryDashboard;
            case UserPermissions.SummaryDashboard_CompareView:
                return Localization.SummaryDashboardCompareView;
            case UserPermissions.SummaryDashboard_BrandView:
                return Localization.SummaryDashboardBrandView;
            case UserPermissions.UserManagementAccess:
                return Localization.UserManagement;
            case UserPermissions.SummaryDashboard_ViewOtherAdvisors:
                return Localization.SummaryDashboardViewOtherAdvisors;
            case UserPermissions.SummaryDashboard_SetChangeGoals:
                return Localization.SummaryDashboardSetChangeGoals;
            case UserPermissions.UserManagement_AddUsers:
                return Localization.UserManagementAddUsers;
            case UserPermissions.UserManagement_DeleteUsers:
                return Localization.UserManagementDeleteUsers;
            case UserPermissions.UserManagement_GeneratePasswords:
                return Localization.UserManagementGeneratePasswords;
            case UserPermissions.KeyManagement_Mobile:
                return Localization.KeyManagementMobile;
            case UserPermissions.QRCodeScan_Mobile:
                return Localization.QRCodeScanMobile;
            case UserPermissions.ActivityDashboard_ROAddEditAfterPrintedReceipt:
                return Localization.ActivityDashboardROAddEditAfterPrintedReceipt;
            case UserPermissions.Settings_GeneralSettings:
                return Localization.SettingsGeneralSettings;
            case UserPermissions.Settings_DisclaimersAndConsents:
                return Localization.SettingsDisclaimersAndConsents;
            case UserPermissions.Settings_Communications:
                return Localization.SettingsCommunications;
            case UserPermissions.SupportKiosk:
                return Localization.Kiosk;
            case UserPermissions.SupportHealthCheck:
                return Localization.HealthCheck;
            case UserPermissions.SupportUserManagement:
                return Localization.UserManagement;
            case UserPermissions.SupportAddKiosk:
                return Localization.KioskAdd;
            case UserPermissions.SupportDeleteKiosk:
                return Localization.KioskDelete;
            case UserPermissions.SupportUserManagementAddUser:
                return Localization.UserManagementAdd;
            case UserPermissions.SupportUserManagementDeleteUser:
                return Localization.UserManagementDelete;
            case UserPermissions.SupportDashboard:
                return Localization.Dashboard;
            case UserPermissions.SupportResponseLog:
                return Localization.ResponseLog;
            case UserPermissions.SupportKioskBanner:
                return Localization.KioskBanner;
            case UserPermissions.SupportAddKioskBanner:
                return Localization.KioskBannerAdd;
            case UserPermissions.SupportDeleteKioskBanner:
                return Localization.KioskBannerDelete;
            case UserPermissions.SupportDealerGroup:
                return Localization.DealerGroup;
            case UserPermissions.SupportAddDealerGroup:
                return Localization.DealerGroupAdd;
            case UserPermissions.SupportDeleteDealerGroup:
                return Localization.DealerGroupDelete;
            case UserPermissions.SupportDealerSettings:
                return Localization.DealerSettings;
            case UserPermissions.SupportDeleteDealerSettings:
                return Localization.DealerSettingsDelete;
            case UserPermissions.SupportFortellisSwapTables:
                return Localization.FortellisSwapTables;
            case UserPermissions.SupportReports:
                return Localization.Reports;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be later called.
 * When the function is called, it takes an audit trail event as an action and
 * the mergeField associated with that action. From those props, it returns a localized string
 * to display on screen.
 *
 * @returns Callback Function - {@link GetUserChangeHistoryLabelHook Callback Function} type definition.
 */
export const useGetUserChangeHistoryLabel: GetUserChangeHistoryLabelHook = () => {
    const Localization = useLocalization();
    const { RunningAppType } = useAppCoreState();

    return useCallback(({
        action,
        mergeField,
    }) => {
        switch (action) {
            case UserChangeActionTypes.UserAdded:
                return Localization.UserAdded;
            case UserChangeActionTypes.UserLocked:
                return Localization.UserLocked;
            case UserChangeActionTypes.UserUnlocked:
                return Localization.UserUnlocked;
            case UserChangeActionTypes.UserActivated:
                return Localization.UserActivated;
            case UserChangeActionTypes.UserInactivated:
                return Localization.UserInactivated;
            case UserChangeActionTypes.PermissionsUpdated:
                return Localization.PermissionsUpdated;
            case UserChangeActionTypes.NotificationChoicesUpdated:
                return Localization.NotificationChoicesUpdated;
            case UserChangeActionTypes.PinUpdated:
                return Localization.PinUpdated;
            case UserChangeActionTypes.AdvisorNumberUpdated:
                return Localization.AdvisorNumberUpdated;
            case UserChangeActionTypes.EmailAddressUpdated:
                return Localization.EmailAddressUpdated;
            case UserChangeActionTypes.NameUpdated:
                return Localization.NameUpdated;
            case UserChangeActionTypes.PhoneUpdated:
                return Localization.PhoneUpdated;
            case UserChangeActionTypes.PasswordGenerated:
                return Localization.PasswordGenerated;
            case UserChangeActionTypes.DSIMIdUpdated:
                return Localization.dlrSecuredIdUpdated;
            case UserChangeActionTypes.RoleUpdated:
                return Localization.RoleUpdated;
            case UserChangeActionTypes.LanguageUpdated:
                return Localization.LanguageUpdated;
            case UserChangeActionTypes.TextOptInUpdated:
                return Localization.TextingOptInUpdated;
            case UserChangeActionTypes.TextOptIn:
                if (RunningAppType === AppType.Dashboard && mergeField) {
                    const [phoneNumber, ...dealers]: string[] = mergeField.split(',');
                    return Localization.formatString(Localization.TextOptInMerge, formatPhoneNumber(phoneNumber), dealers.join(', ')) as string;
                }
                else {
                    return Localization.TextOptIn;
                }
            case UserChangeActionTypes.TextOptOut:
                if (RunningAppType === AppType.Dashboard && mergeField) {
                    const [phoneNumber, ...dealers]: string[] = mergeField.split(',');
                    return Localization.formatString(Localization.TextOptOutMerge, formatPhoneNumber(phoneNumber), dealers.join(', ')) as string;
                }
                else {
                    return Localization.TextOptOut;
                }
            case UserChangeActionTypes.TextError:
                return Localization.TextError;
            case UserChangeActionTypes.DealerInactiveNotificationsDisabled:
                if (mergeField) {
                    return Localization.formatString(Localization.DealerInactiveNotificationsDisabledMerge, mergeField) as string;
                }
                else {
                    return Localization.DealerInactiveNotificationsDisabled;
                }
            case UserChangeActionTypes.TextOptOutInstructionsSent:
                return Localization.TextOptOutInstructionsSent;
            case UserChangeActionTypes.ROReminderEmailChoicesUpdated:
                return Localization.ROReminderEmailChoicesUpdated;
            case UserChangeActionTypes.ROReminderTextChoicesUpdated:
                return Localization.ROReminderTextChoicesUpdated;
            default:
                return '';
        }
    }, [Localization, RunningAppType]);
};

export const useGetLoginLocationLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: LoginLocation) => {
        switch (e) {
            case LoginLocation.Dashboard:
                return Localization.Dashboard;
            case LoginLocation.Kiosk:
                return Localization.Kiosk;
            case LoginLocation.Support:
                return Localization.Support;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetCountryLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: Country) => {
        switch (e) {
            case Country.Canada:
                return Localization.Canada;
            case Country.US:
                return Localization.US;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetTimezoneLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: Timezone) => {
        switch (e) {
            case Timezone.Default:
            case Timezone.Eastern:
                return Localization.Eastern;
            case Timezone.Alaska:
                return Localization.Alaska;
            case Timezone.Arizona:
                return Localization.Arizona;
            case Timezone.Central:
                return Localization.Central;
            case Timezone.Hawaii:
                return Localization.Hawaii;
            case Timezone.Mountain:
                return Localization.Mountain;
            case Timezone.Pacific:
                return Localization.Pacific;
            case Timezone.Atlantic:
                return Localization.Atlantic;
            case Timezone.Newfoundland:
                return Localization.Newfoundland;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetDealerStatusLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: DealerStatus) => {
        switch (e) {
            case DealerStatus.Inactive:
                return Localization.Inactive;
            case DealerStatus.Active:
                return Localization.Active;
            case DealerStatus.PreInstall:
                return Localization.PreInstall;
            case DealerStatus.Test:
                return Localization.Test;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetRecallTypeLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: RecallType) => {
        switch (e) {
            case RecallType.Master:
                return Localization.RecallMasters;
            case RecallType.VW:
                return Localization.Volkswagen;
            case RecallType.KIA:
                return Localization.KIA;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetAppointmentTypeLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: AppointmentType) => {
        switch (e) {
            case AppointmentType.Service:
                return Localization.Service;
            case AppointmentType.BodyShop:
                return Localization.BodyShop;
            case AppointmentType.QuickLube:
                return Localization.QuickLube;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetHistoryActionTypeLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: HistoryActionType) => {
        switch (e) {
            case HistoryActionType.Added:
                return Localization.Added;
            case HistoryActionType.Changed:
                return Localization.Changed;
            case HistoryActionType.Deleted:
                return Localization.Deleted;
            case HistoryActionType.SoftwareUpdated:
                return Localization.Updated;
            case HistoryActionType.Action:
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetPaymentProviderLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: PaymentProvider) => {
        switch (e) {
            case PaymentProvider.StripeCanada:
                return Localization.StripeCanada;
            case PaymentProvider.WorldPayUS:
                return Localization.WorldPayUS;
            default:
                return '';
        }
    }, [Localization]);
};


export const useGetLanguageName = () => {
    const Localization = useLocalization();

    return useCallback((lang?: DealerGeneralSettings['alternateLanguage']) => {
        switch (lang) {
            case 'en-us':
                return Localization.English;
            case 'es-us':
                return Localization.Spanish;
            case 'fr-ca':
                return Localization.French;
        }

        return Localization.None;
    }, [Localization]);
};


export const useGetPaymentStatusLabel = () => {
    const Localization = useLocalization();

    return useCallback((paymentStatus: Appointment['paymentStatus']) => {
        switch (paymentStatus) {
            case 'not paid':
                return Localization.PaymentRequired;
            case 'paid':
                return Localization.PaymentReceived;
            default:
                return Localization.MarkedPaid;
        }
    }, [Localization]);
};

export const useGetTransportationName = () => {
    const Localization = useLocalization();

    return useCallback((option: Appointment['transportation']) => {
        if (option) {
            switch (option.type) {
                case TransportationOptionType.DropOff:
                    return Localization.DroppingOff;
                case TransportationOptionType.Loaner:
                    switch (option.dmsTransportationType) {
                        case AppointmentLoanerOrRentalOption.Loaner:
                            return Localization.Loaner;
                        case AppointmentLoanerOrRentalOption.Rental:
                            return Localization.Rental;
                        default:
                            return Localization.PreBookedRentalLoaner;
                    }
                case TransportationOptionType.Shuttle:
                    if (option.subtype === TransportationOptionSubtype.Uber) {
                        return Localization.RequestUber;
                    }
                    if (option.subtype === TransportationOptionSubtype.Lyft) {
                        return Localization.RequestLyft;
                    }
                    return Localization.RequestShuttle;
                case TransportationOptionType.Waiter:
                    return Localization.WaitHere;
                default:
                    return Localization.NotNeeded;
            }
        }

        return '';
    }, [Localization]);
};

export const useGetBusinessTypeLabel = () => {
    const Localization = useLocalization();

    return useCallback((e: BusinessType) => {
        switch (e) {
            case BusinessType.AssociationEstateTrust:
                return Localization.AssociationEstateTrust;
            case BusinessType.FINInstitute:
                return Localization.FINInstitution;
            case BusinessType.Government:
                return Localization.Government;
            case BusinessType.LLC:
                return Localization.LLC;
            case BusinessType.LLP:
                return Localization.LLP;
            case BusinessType.NonProfit:
                return Localization.NonProfit;
            case BusinessType.Partnership:
                return Localization.Partnership;
            case BusinessType.PrivateCorporation:
                return Localization.PrivateCorporation;
            case BusinessType.ProfessionalCorporation:
                return Localization.ProfessionalCorporation;
            case BusinessType.PublicCorporation:
                return Localization.PublicCorporation;
            case BusinessType.SCorporation:
                return Localization.SCorporation;
            case BusinessType.SECRegistered:
                return Localization.SECRegistered;
            case BusinessType.SoleProprietor:
                return Localization.SoleProprieter;
            case BusinessType.Other:
                return Localization.Other;
            case BusinessType.None:
            default:
                return '';
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be later called.
 * When the function is called, it takes an RODepartment enum value.
 * From this prop, it returns a localized string to display on screen.
 *
 * @returns Callback Function - {@link GetRoDepartmentLabelHook Callback Function} type definition.
 */
export const useGetRoDepartmentLabel: GetRoDepartmentLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ roDepartment }) => {
        switch (roDepartment) {
            case RODepartment.Service:
                return Localization.Service;
            case RODepartment.BodyShop:
                return Localization.BodyShop;
            case RODepartment.PDI:
                return Localization.PDI_IGNORE;
            case RODepartment.None:
            default:
                return Localization.None;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be later called.
 * When the function is called, it takes an WalkInDepartment enum value.
 * From this prop, it returns a localized string to display on screen.
 *
 * @returns Callback Function - {@link GetWalkInDepartmentLabelHook Callback Function} type definition.
 */
export const useGetWalkInDepartmentLabel: GetWalkInDepartmentLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ walkInDepartment }) => {
        switch (walkInDepartment) {
            case WalkInDepartment.Service:
                return Localization.Service;
            case WalkInDepartment.OneStep:
                return Localization.OneStep_IGNORE;
            case WalkInDepartment.BodyShop:
                return Localization.BodyShop;
            case WalkInDepartment.PDI:
                return Localization.PDI_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 *
 */
export const useGetMeridiemLabel: GetMeridiemLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((meridiem) => {
        switch (meridiem) {
            case Meridiem.AM:
                return Localization.AM_IGNORE;
            case Meridiem.PM:
                return Localization.PM_IGNORE;
            default:
                return '';
        }
    }, [Localization.AM_IGNORE, Localization.PM_IGNORE]);
};

export const useGetWalkInsOptionsLabel: GetWalkInsOptionsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ walkInsOption }) => {
        switch (walkInsOption) {
            case WalkInsOption.None:
                return Localization.None;
            case WalkInsOption.ExistingCustomersOnly:
                return Localization.ExistingCustomersOnly_IGNORE;
            case WalkInsOption.All:
                return Localization.All;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetMobilePathOptionsLabel: GetMobilePathOptionsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ mobilePath }) => {
        switch (mobilePath) {
            case MobilePathOption.OneStep:
                return Localization.OneStep_IGNORE;
            case MobilePathOption.TwoStep:
                return Localization.TwoStep_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetKeyDropoffOptionsLabel: GetKeyDropoffOptionsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ keyDropoffOption }) => {
        switch (keyDropoffOption) {
            case KeyDropoffOption.On:
                return Localization.KeyDropoffOn_IGNORE;
            case KeyDropoffOption.Off:
                return Localization.KeyDropoffOff_IGNORE;
            case KeyDropoffOption.AfterHours:
                return Localization.KeyDropoffAfterHours_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetVinOptionsLabel: GetVinOptionsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ vinOption }) => {
        switch (vinOption) {
            case VinOption.None:
                return Localization.None;
            case VinOption.Optional:
                return Localization.Optional_IGNORE;
            case VinOption.Required:
                return Localization.Required_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetTagNumberOptionsLabel: GetTagNumberOptionsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback(({ tagNumberOption }) => {
        switch (tagNumberOption) {
            case TagNumberOption.None:
                return Localization.None;
            case TagNumberOption.Optional:
                return Localization.Optional_IGNORE;
            case TagNumberOption.Required:
                return Localization.Required_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 *
 */
export const useGetDayOfWeekLabel: GetDayOfWeekLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((day: DayOfWeek, locale: string) => {
        switch (day) {
            case DayOfWeek.Monday:
                return Localization.formatString(Localization.Monday, locale) as string;
            case DayOfWeek.Tuesday:
                return Localization.formatString(Localization.Tuesday, locale) as string;
            case DayOfWeek.Wednesday:
                return Localization.formatString(Localization.Wednesday, locale) as string;
            case DayOfWeek.Thursday:
                return Localization.formatString(Localization.Thursday, locale) as string;
            case DayOfWeek.Friday:
                return Localization.formatString(Localization.Friday, locale) as string;
            case DayOfWeek.Saturday:
                return Localization.formatString(Localization.Saturday, locale) as string;
            case DayOfWeek.Sunday:
                return Localization.formatString(Localization.Sunday, locale) as string;
            default:
                return '';
        }
    }, [Localization]);
};

/*
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a KioskStatus enumeration value.
 * From the enumeration value, it returns a localized string to display on screen.
 *
 * @returns Callback Function - {@link GetKioskStatusLabelHook Callback Function} type definition.
 */
export const useGetKioskStatusLabel: GetKioskStatusLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((kioskStatus: KioskStatus) => {
        switch (kioskStatus) {
            case KioskStatus.Both:
                return Localization.Both;
            case KioskStatus.Active:
                return Localization.Active;
            case KioskStatus.Inactive:
                return Localization.Inactive;
            default:
                return '';
        }
    }, [Localization.Active, Localization.Both, Localization.Inactive]);
};

/**
 * This hook takes no props, but returns a function that can be later called.
 * When the function is called, it takes an HunterTestResult enum value.
 * From this prop, it returns a localized string to display on screen.
 *
 * @returns Callback Function - {@link GetHunterTestResultLabelHook} type definition.
 */
export const useGetHunterTestResultLabel: GetHunterTestResultLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((testResult: HunterTestResult) => {
        switch (testResult) {
            case HunterTestResult.Passed:
                return Localization.Passed_IGNORE;
            case HunterTestResult.Failed:
                return Localization.Failed_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link HunterResult} object and returns
 * the localized string to display on the screen.
 */
export const useGetHunterResultLabel: GetHunterResultLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((hunterResult: HunterResult) => {
        switch (hunterResult) {
            case HunterResult.NoResult:
                return Localization.NoResult_IGNORE;
            case HunterResult.Good:
                return Localization.Good_IGNORE;
            case HunterResult.Warning:
                return Localization.Warning_IGNORE;
            case HunterResult.Severe:
                return Localization.Severe_IGNORE;
            case HunterResult.Danger:
                return Localization.Danger_IGNORE;
            case HunterResult.TestAborted:
                return Localization.TestAborted_IGNORE;
            case HunterResult.TestError:
                return Localization.TestError_IGNORE;
            case HunterResult.TestTimeout:
                return Localization.TestTimeout_IGNORE;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link HunterAuditResult} object and returns
 * the localized string to display on the screen.
 */
export const useGetHunterAuditResultLabel: GetHunterAuditResultLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((testResult: HunterAuditResult) => {
        switch (testResult) {
            case HunterAuditResult.Passed:
                return Localization.Passed_IGNORE;
            case HunterAuditResult.Recommended:
                return Localization.Recommended_IGNORE;
            default:
                return Localization.NA_IGNORE;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link SpgPricing} object and returns
 * the localized string to display on the screen.
 */
export const useGetSpgPricingLabel: GetSpgPricingLabel = () => {
    const Localization = useLocalization();

    return useCallback((spgPricing: SpgPricing) => {
        switch (spgPricing) {
            case SpgPricing.Range:
                return Localization.Range;
            case SpgPricing.Minimum:
                return Localization.Minimum_IGNORE;
            case SpgPricing.Maximum:
                return Localization.Maximum_IGNORE;
            case SpgPricing.NoPrice:
                return Localization.NoPrice_IGNORE;
            default:
                return Localization.NA_IGNORE;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link ServiceCategory} object and returns
 * the localized string to display on the screen.
 */
export const useGetServiceCategoryLabel: GetServiceCategoryLabel = () => {
    const Localization = useLocalization();

    return useCallback((category: ServiceCategory) => {
        switch (category) {
            case ServiceCategory.All:
                return Localization.All;
            case ServiceCategory.AirConditioner:
                return Localization.AirConditioner_IGNORE;
            case ServiceCategory.AirFiltration:
                return Localization.AirFiltration_IGNORE;
            case ServiceCategory.Alignment:
                return Localization.Alignment_IGNORE;
            case ServiceCategory.Battery:
                return Localization.Battery_IGNORE;
            case ServiceCategory.Brakes:
                return Localization.Brakes_IGNORE;
            case ServiceCategory.CoolingSystem:
                return Localization.CoolingSystem_IGNORE;
            case ServiceCategory.Drivetrain:
                return Localization.Drivetrain_IGNORE;
            case ServiceCategory.Inspection:
                return Localization.Inspection_IGNORE;
            case ServiceCategory.Emissions:
                return Localization.Emissions_IGNORE;
            case ServiceCategory.Engine:
                return Localization.Engine_IGNORE;
            case ServiceCategory.FuelSystem:
                return Localization.FuelSystem_IGNORE;
            case ServiceCategory.Lights:
                return Localization.Lights_IGNORE;
            case ServiceCategory.Wipers:
                return Localization.Wipers_IGNORE;
            case ServiceCategory.Oil:
                return Localization.Oil_IGNORE;
            case ServiceCategory.Recall:
                return Localization.Recall_IGNORE;
            case ServiceCategory.Suspension:
                return Localization.Suspension_IGNORE;
            case ServiceCategory.Tires:
                return Localization.Tires_IGNORE;
            case ServiceCategory.Transmission:
                return Localization.Transmission_IGNORE;
            case ServiceCategory.Other:
                return Localization.Other;
            case ServiceCategory._5000K:
                return Localization['5000K_IGNORE'];
            case ServiceCategory._7500K:
                return Localization['7500K_IGNORE'];
            case ServiceCategory._10000K:
                return Localization['10000K_IGNORE'];
            case ServiceCategory._15000K:
                return Localization['15000K_IGNORE'];
            case ServiceCategory._20000K:
                return Localization['20000K_IGNORE'];
            case ServiceCategory._30000K:
                return Localization['30000K_IGNORE'];
            case ServiceCategory._40000K:
                return Localization['40000K_IGNORE'];
            case ServiceCategory._45000K:
                return Localization['45000K_IGNORE'];
            case ServiceCategory._60000K:
                return Localization['60000K_IGNORE'];
            case ServiceCategory._75000K:
                return Localization['75000K_IGNORE'];
            case ServiceCategory._80000K:
                return Localization['80000K_IGNORE'];
            case ServiceCategory._90000K:
                return Localization['90000K_IGNORE'];
            case ServiceCategory._100000K:
                return Localization['100000K_IGNORE'];
            case ServiceCategory._120000K:
                return Localization['120000K_IGNORE'];
            case ServiceCategory._160000K:
                return Localization['160000K_IGNORE'];
            case ServiceCategory.ScheduledService:
                return Localization.ScheduledService_IGNORE;
            case ServiceCategory.Maintenance:
                return Localization.Maintenance_IGNORE;
            case ServiceCategory.PDI:
                return Localization.PDI_IGNORE;
            case ServiceCategory.UCINotCertified:
                return Localization.UciNotCertified_IGNORE;
            case ServiceCategory.UCICertified:
                return Localization.UciCertified_IGNORE;
            default:
                return Localization.NA_IGNORE;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link ServiceTypeValues} value and returns
 * the localized string to display on the screen.
 */
export const useGetServiceTypeLabel: GetServiceTypeLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((serviceType: ServiceTypeValues) => {
        switch (serviceType) {
            case ServiceType.General:
                return Localization.General_IGNORE;
            case ServiceType.UpsellService:
                return Localization.UpsellService_IGNORE;
            case ServiceType.UpsellAccessory:
                return Localization.UpsellAccessory_IGNORE;
            case ServiceType.Miscellaneous:
                return Localization.Miscellaneous_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link UnassignedServicesFilterValues} value and returns
 * the localized string to display on the Support Services screen's "Unassigned Services" filter.
 */
export const useGetUnassignedServicesFilterLabel: GetUnassignedServicesFilterLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((unassignedFilter: UnassignedServicesFilterValues) => {
        switch (unassignedFilter) {
            case UnassignedServicesFilter.All:
                return Localization.All_IGNORE;
            case UnassignedServicesFilter.Unassigned:
                return Localization.Unassigned_IGNORE;
            case UnassignedServicesFilter.Assigned:
                return Localization.Assigned_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

/*
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link KpiReportViewType} object and returns
 * the localized string to display on the screen.
 */
export const useGetKpiReportViewTypeLabels: GetKpiReportViewTypeLabelsHook = () => {
    const Localization = useLocalization();

    return useCallback((viewType: Parameters<ReturnType<GetKpiReportViewTypeLabelsHook>>['0']) => {
        switch (viewType) {
            case KpiReportViewType.Dealer:
                return Localization.DealerView;
            case KpiReportViewType.Kiosk:
                return Localization.KioskView_IGNORE;
            default:
                return Localization.NA_IGNORE;
        }
    }, [Localization]);
};

/*
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link KpiReportMetricType} object and returns
 * the localized string to display on the screen.
 */
export const useGetKpiReportMetricTypeLabels: GetKpiReportMetricTypeLabelsHook = () => {
    const Localization = useLocalization();

    return useCallback((metricType: Parameters<ReturnType<GetKpiReportMetricTypeLabelsHook>>['0']) => {
        switch (metricType) {
            case KpiReportMetricType.Counts:
                return Localization.Counts_IGNORE;
            case KpiReportMetricType.PercentChange:
                return Localization.PercentChange_IGNORE;
            case KpiReportMetricType.PercentDmsAppointments:
                return Localization.PercentOfDmsAppointments_IGNORE;
            case KpiReportMetricType.Rates:
                return Localization.Rates_IGNORE;
            default:
                return Localization.NA_IGNORE;
        }
    }, [Localization]);
};

// #region Valuation Labels

export const useGetBlackbookAdjustmentMethodLabel: GetBlackbookAdjustmentMethodLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((method: BlackbookAdjustmentMethod | null | undefined) => {
        switch (method) {
            case BlackbookAdjustmentMethod.Percentage:
                return Localization.Percentage_IGNORE;
            case BlackbookAdjustmentMethod.FlatRate:
                return Localization.FlatRate_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetValuationScreenLabel: GetValuationScreenLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((screen: ValuationScreen | null | undefined) => {
        switch (screen) {
            case ValuationScreen.None:
                return Localization.None;
            case ValuationScreen.Blackbook:
                return Localization.Blackbook_IGNORE;
            case ValuationScreen.EquityDetected:
                return Localization.EquityDetected_IGNORE;
            case ValuationScreen.ValueSplash_BMWHONOLULU:
                return 'ValueSplash_BMWHONOLULU.jpg';
            case ValuationScreen.ValueSplash_MINIHAWAII:
                return 'ValueSplash_MINIHAWAII.jpg';
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetMobileValuationScreenLabel: GetMobileValuationScreenLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((screen: MobileValuationScreen | null | undefined) => {
        switch (screen) {
            case MobileValuationScreen.Blackbook:
                return Localization.Blackbook_IGNORE;
            case MobileValuationScreen.EquityDetected:
                return Localization.EquityDetected_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetCRMProviderLabel: GetCRMProviderLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((provider: CRMProvider | null | undefined) => {
        switch (provider) {
            case CRMProvider.None:
                return Localization.None;
            case CRMProvider.Reynolds:
                return Localization.Reynolds;
            case CRMProvider.Affinitiv:
                return Localization.Affinitiv_IGNORE;
            case CRMProvider.DealerSocket:
                return Localization.DealerSocket_IGNORE;
            case CRMProvider.eLeads:
                return Localization.ELeads_IGNORE;
            case CRMProvider.GmGlobalConnect:
                return Localization.GmGlobalConnect_IGNORE;
            case CRMProvider.VinSolutions:
                return Localization.VinSolutions_IGNORE;
            case CRMProvider.Other:
                return Localization.Other;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetTransportationOptionSubtypeLabel: GetTransportationOptionSubtypeLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((subtype: TransportationOptionSubtype | null | undefined) => {
        switch (subtype) {
            case TransportationOptionSubtype.Shuttle:
                return Localization.Shuttle;
            case TransportationOptionSubtype.Uber:
                return Localization.Uber_IGNORE;
            case TransportationOptionSubtype.Lyft:
                return Localization.Lyft_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};

export const useGetTransportationOptionTypeLabel: GetTransportationOptionTypeLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((type: TransportationOptionType) => {
        switch (type) {
            case TransportationOptionType.DropOff:
                return Localization.DropOff;
            case TransportationOptionType.Waiter:
                return Localization.Waiter;
            case TransportationOptionType.Loaner:
                return Localization.PreBookedRentalLoaner;
            case TransportationOptionType.Shuttle:
                return Localization.Custom;
            default:
                return '';
        }
    }, [Localization]);
};

// #endregion

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes an {@link AppointmentNotificationName} value
 * and returns the localized string to display on the screen.
 */
export const useGetAppointmentNotificationNameLabel: GetAppointmentNotificationNameLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((name: AppointmentNotificationName) => {
        switch (name) {
            case AppointmentNotificationName.AppointmentReminder:
                return Localization.NotificationName_AppointmentReminder;
            case AppointmentNotificationName.CheckInConfirmation:
                return Localization.NotificationName_CheckInConfirmation;
            case AppointmentNotificationName.OnlineCheckInConfirmed:
                return Localization.NotificationName_OnlineCheckInConfirmed;
            case AppointmentNotificationName.OnlineCheckInSaved:
                return Localization.NotificationName_OnlineCheckInSaved;
            case AppointmentNotificationName.PaymentDue:
                return Localization.NotificationName_PaymentDue;
            case AppointmentNotificationName.ReadyForPickup:
                return Localization.NotificationName_ReadyForPickup;
            case AppointmentNotificationName.PickupReceipt:
                return Localization.NotificationName_PickupReceipt;
            case AppointmentNotificationName.PhoneUpdated:
                return Localization.NotificationName_PhoneUpdated;
            case AppointmentNotificationName.MFAEmailUpdate:
                return Localization.NotificationName_MFAEmailUpdate;
            default:
                return '';
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a provider-reported delivery status string
 * and returns the localized string to display. Matching is case-insensitive because
 * the status arrives Title-Cased in some flows and lowercased in others. Unmapped
 * values fall back to the raw string.
 */
export const useGetNotificationDeliveryStatusLabel: GetNotificationDeliveryStatusLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((status: string) => {
        switch (status.toLowerCase()) {
            case 'queued':
                return Localization.Queued;
            case 'sent':
                return Localization.Sent;
            case 'delivered':
                return Localization.Delivered;
            case 'opened':
                return Localization.Opened;
            case 'clicked':
                return Localization.Clicked;
            case 'undelivered':
                return Localization.Undelivered;
            case 'failed':
                return Localization.Failed;
            case 'mailbox full':
                return Localization.MailboxFull;
            case 'temporary failure':
                return Localization.TemporaryFailure;
            case 'domain hold':
                return Localization.DomainHold;
            case 'bad address':
                return Localization.BadAddress;
            case 'bad domain':
                return Localization.BadDomain;
            case 'content rejection':
                return Localization.ContentRejection;
            case 'provider complaint':
                return Localization.ProviderComplaint;
            case 'spamtrap':
                return Localization.Spamtrap;
            case 'greylisted':
                return Localization.Greylisted;
            case 'blacklisted':
                return Localization.Blacklisted;
            case 'deferred':
                return Localization.Deferred;
            default:
                return status;
        }
    }, [Localization]);
};

/**
 * This hook takes no props, but returns a function that can be called later.
 * When the function is called, it takes a {@link NotificationDeliveryStatusViewAs} value
 * and returns the localized string to display on the screen.
 */
export const useGetNotificationDeliveryStatusViewAsLabel: GetNotificationDeliveryStatusViewAsLabelHook = () => {
    const Localization = useLocalization();

    return useCallback((viewAs: NotificationDeliveryStatusViewAs) => {
        switch (viewAs) {
            case NotificationDeliveryStatusViewAs.Counts:
                return Localization.Counts_IGNORE;
            case NotificationDeliveryStatusViewAs.Percentages:
                return Localization.Percentages_IGNORE;
            default:
                return '';
        }
    }, [Localization]);
};